import React, { createContext, useCallback, useContext, useState, useEffect } from "react";
import { useIndexedDBContext } from "./indexed-db-provider";

interface LogEntry {
    id: string;
    type: "error" | "info" | "warning";
    message: string;
    stack?: string;
    timestamp: number;
}

interface LogContextType {
    logs: LogEntry[];
    addLog: (log: Omit<LogEntry, "id" | "timestamp">) => void;
}

const LogContext = createContext<LogContextType | undefined>(undefined);

export const useLogContext = () => {
    const context = useContext(LogContext);
    if (!context) {
        throw new Error("useLogContext must be used within a LogProvider");
    }
    return context;
};

export const LogProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
    const [logs, setLogs] = useState<LogEntry[]>([]);
    const { logsDb } = useIndexedDBContext();

    const loadLogs = useCallback(async () => {
        try {
            const allLogs = await logsDb.getAllItems();
            setLogs(allLogs.sort((a, b) => b.timestamp - a.timestamp));
        } catch (error) {
            console.error("[LogProvider] Error loading logs:", error);
        }
    }, [logsDb]);

    useEffect(() => {
        if (logsDb.isLoading || logs.length > 0) return;
        loadLogs();
    }, [logsDb.isLoading, loadLogs, logs.length]);

    const addLog = useCallback(
        async (log: Omit<LogEntry, "id" | "timestamp">) => {
            const newLog: LogEntry = {
                ...log,
                id: crypto.randomUUID(),
                timestamp: Date.now(),
            };

            try {
                await logsDb.addItem(newLog);

                setLogs((prev) => [newLog, ...prev]);
            } catch (error) {
                console.error("[LogProvider] Failed to add log:", error);
            }
        },
        [logsDb]
    );

    return <LogContext.Provider value={{ logs, addLog }}>{children}</LogContext.Provider>;
};
